/**
 * Bath2U custom variant dropdown integration for Shopify Horizon.
 * Keeps the styled <details> menu in sync with Horizon's native <select>.
 * When a custom option is clicked, dispatches a bubbling change event so
 * assets/variant-picker.js can update variant ID, price, media, and product form.
 */
class BathVariantSelect extends HTMLElement {
  constructor() {
    super();
    this._onClick = this.onClick.bind(this);
    this._onChange = this.onChange.bind(this);
    this._observer = new MutationObserver(() => {
      // Horizon morphs updated picker markup; restore enhancement as needed.
      if (!this.hasAttribute('data-enhanced')) {
        this.setAttribute('data-enhanced', '');
      }
      this.syncFromSelect();
    });
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.setAttribute('data-enhanced', '');
    this.syncFromSelect();

    // Handle new <option> / custom button nodes inserted by Horizon morphing.
    this._observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-enhanced', 'selected'],
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
    this._observer.disconnect();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.bath-variant-menu__option');
    if (!button || !this.contains(button)) return;

    const select = this.querySelector('select.variant-option__select');
    if (!(select instanceof HTMLSelectElement)) return;

    const index = Number(button.dataset.selectIndex);
    if (!Number.isInteger(index) || index < 0 || index >= select.options.length) return;

    event.preventDefault();
    const changed = select.selectedIndex !== index;
    select.selectedIndex = index;
    this.syncFromSelect();

    const menu = this.querySelector('.bath-variant-menu');
    if (menu instanceof HTMLDetailsElement) menu.open = false;

    if (changed) {
      // Critical: Horizon's <variant-picker> listens for this event.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onChange(event) {
    if (event.target instanceof HTMLSelectElement && this.contains(event.target)) {
      this.syncFromSelect();
    }
  }

  syncFromSelect() {
    const select = this.querySelector('select.variant-option__select');
    if (!(select instanceof HTMLSelectElement)) return;

    const index = select.selectedIndex;
    if (index < 0) return;

    const selectedValue = select.options[index]?.value || '';
    const valueLabel = this.querySelector('.bath-variant-menu__value');
    if (valueLabel && valueLabel.textContent !== selectedValue) {
      valueLabel.textContent = selectedValue;
    }

    const trigger = this.querySelector('.bath-variant-menu__trigger');
    const optionLabel = this.closest('.variant-option--dropdowns')?.querySelector('label')?.textContent?.trim() || 'Variant';
    const accessibleLabel = `${optionLabel}: ${selectedValue}`;
    if (trigger?.getAttribute('aria-label') !== accessibleLabel) {
      trigger?.setAttribute('aria-label', accessibleLabel);
    }

    this.querySelectorAll('.bath-variant-menu__option').forEach((button, i) => {
      const selected = String(i === index);
      if (button.getAttribute('aria-pressed') !== selected) {
        button.setAttribute('aria-pressed', selected);
      }
    });
  }
}

if (!customElements.get('bath-variant-select')) {
  customElements.define('bath-variant-select', BathVariantSelect);
}
